import { isAndroid } from "@/libs/software"

type Delegate = "CPU" | "GPU"

export const getInferenceDelegate = (): Delegate => (guessIsNeedCPUDelegate() ? "CPU" : "GPU")

/**
 * There is a bug on some devices that breaks any detections when GPU delegate is used.
 * Affected devices are samsung Galaxy S25 family, Xiaomi 15 and other devices with Adreno 830 GPU and newer.
 * This is a guesser function that narrows down the device's gpu/navigator/window properties
 * and returns true if they resemble the affected devices (which were received using real devices, lambdatest and information from the related issues on github).
 *
 * Issues links:
 * - https://github.com/google-ai-edge/mediapipe/issues/5908
 * - https://github.com/google-ai-edge/mediapipe/issues/5867
 */
const guessIsNeedCPUDelegate = (): boolean => {
  if (!isAndroid) return false

  const ua = navigator.userAgent.toLowerCase()
  const gpu = readGpuInfo()

  if (gpu) {
    if (isAdreno830OrNewer(gpu)) return true
    return false
  }

  if (isGalaxyS25FamilyUAOrScreen(ua)) return true
  if (isXiaomi15FamilyUA(ua)) return true

  if (isKnown8EliteFamilyUA(ua)) return true

  return false
}

const ADRENO_PROBLEM_MIN_VERSION = 830

const isAdreno830OrNewer = ({
  vendor,
  renderer,
}: {
  vendor: string
  renderer: string
}): boolean => {
  const v = vendor.toLowerCase()
  const r = renderer.toLowerCase()

  const isQualcommLike = v.includes("qualcomm") || r.includes("adreno")
  if (!isQualcommLike) return false

  const adreno = extractAdrenoVersion(r)
  return adreno !== null && adreno >= ADRENO_PROBLEM_MIN_VERSION
}

const extractAdrenoVersion = (renderer: string): number | null => {
  const m = renderer.match(/adreno[^\d]*?(\d{3,4})/i)
  if (!m) return null
  const num = parseInt(m[1]!, 10)
  return Number.isNaN(num) ? null : num
}

const readGpuInfo = (): { vendor: string; renderer: string } | null => {
  try {
    const canvas = document.createElement("canvas")
    const gl = (canvas.getContext("webgl2") ||
      canvas.getContext("webgl")) as WebGLRenderingContext | null
    if (!gl) return null

    const ext = gl.getExtension("WEBGL_debug_renderer_info") as any
    if (!ext) return null

    const vendor = gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)
    const renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)

    if (typeof vendor !== "string" || typeof renderer !== "string") return null

    return {
      vendor: vendor.trim(),
      renderer: renderer.trim(),
    }
  } catch {
    return null
  }
}

const matchesAnyScreenSize = (pairs: Array<[number, number]>): boolean => {
  const w = screen?.width ?? 0
  const h = screen?.height ?? 0
  if (!w || !h) return false

  return pairs.some(([a, b]) => {
    return (w === a && h === b) || (w === b && h === a)
  })
}

const isGalaxyS25FamilyUAOrScreen = (ua: string): boolean => {
  const uaHit =
    /\bsm-s93(1|6|8)\b/.test(ua) || (ua.includes("samsung") && ua.includes("galaxy s25"))

  // Known screen dimensions for S25 models in portrait mode with CSS pixels
  const screenHit = matchesAnyScreenSize([
    [360, 780], // S25
    [384, 832], // S25+
    [412, 892], // S25 Ultra
    [384, 824],
  ])

  return uaHit || screenHit
}

const isXiaomi15FamilyUA = (ua: string): boolean => {
  return /\bxiaomi[\s_-]?15\b/.test(ua) || /\bxiaomi[\s_-]?15\s+(pro|ultra)\b/.test(ua)
}

const isKnown8EliteFamilyUA = (ua: string): boolean => {
  const patterns: RegExp[] = [
    /\boneplus\s+13\b/,
    /\boneplus\s+13s\b/,
    /\brealme\s+gt\s*7\s*pro\b/,
    /\biqoo\s+13\b/,
    /\bfind\s+x8(\s|$)/,
    /\brog\s+phone\s+9\b/,
    /\bzenfone\s+12\s+ultra\b/,
    /\bred\s+magic\s+10\b/,
    /\bmagic\s*7(\s|$)/,
    /\bxperia\s+1\s+vii\b/,
    /\brazr\s+60\s+ultra\b/,
    /\bpoco\s+f7\s+ultra\b/,
    /\bredmi\s+k80\s+pro\b/,
  ]

  return patterns.some((re) => re.test(ua))
}
